import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR, donutColor } from '../typings/chart';
import { ChartPointModel }                                           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                                          from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                                         from '../typings/intelligence.donut-segment';
import { IntelligenceBrandPerformanceModel }                         from '../typings/intelligence.purchasing';
import { IntelligenceFormatService }                                 from '../intelligence.format.service';
import { IntelligenceListPageModel }                                 from '../typings/intelligence.list-page';
import { IntelligenceGroupComparisonModel }                          from '../typings/intelligence.purchasing';
import { IntelligenceOpenSupplierInvoiceModel }                      from '../typings/intelligence.purchasing';
import { IntelligencePurchasingFiltersModel }                        from '../typings/intelligence.purchasing-filters';
import { IntelligencePurchasingModel }                               from '../typings/intelligence.purchasing';
import { IntelligenceUnallocatedPurchaseModel }                      from '../typings/intelligence.purchasing';
import { IntelligenceService }                                       from '../intelligence.service';
import { IntelligenceTopSupplierModel }                              from '../typings/intelligence.top-supplier';
import { TableSortModel }                                            from '../typings/intelligence.table-sort';

const AGING_BUCKETS = [ 'current', 'days_31_60', 'days_61_90', 'days_90_plus' ];

// Mirrors the backend's table page size; the CSV export asks for pages of 500.
const LIST_PAGE_SIZE = 25;
const CSV_PAGE_SIZE  = 500;

@Component({
  selector   : 'wor-intelligence-purchasing-tab',
  template: require('./intelligence.purchasing-tab.component.html')
})
export class PurchasingTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  agingPoints : Array<ChartPointModel> = [];
  brandRows : Array<IntelligenceBrandPerformanceModel> = [];
  brandSort : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  brandTotals = { cost_ex_tax: 0, gross_profit: 0, revenue_ex_tax: 0 };
  compareSupplier = '';
  comparisonRows : Array<{ a : number; b : number; format : string; key : string }> = [];
  creditsExTax = 0;
  filterOptions : { brands : Array<string>; product_groups : Array<string>; suppliers : Array<string> } = null;
  filters : IntelligencePurchasingFiltersModel = { brand: '', product_group: '', supplier: '' };
  groupComparison : Array<IntelligenceGroupComparisonModel> = [];
  groupPoints : Array<ChartPointModel> = [];
  groupSegments : Array<DonutSegmentModel> = [];
  openInvoices : Array<IntelligenceOpenSupplierInvoiceModel> = [];
  openInvoicesCount = 0;
  openInvoicesPage = 1;
  openInvoicesPaged : Array<IntelligenceOpenSupplierInvoiceModel> = [];
  openInvoicesPaging = false;
  openInvoicesSort : TableSortModel = { direction: 'desc', field: 'post_date' };
  openOrderCount = 0;
  openOrderValue = 0;
  purchaseLines : Array<IntelligenceUnallocatedPurchaseModel> = [];
  purchaseLinesCount = 0;
  purchaseView = 'unallocated';
  purchasesPage = 1;
  purchasesPaged : Array<IntelligenceUnallocatedPurchaseModel> = [];
  purchasesPaging = false;
  purchasesSort : TableSortModel = { direction: 'desc', field: 'value_ex_tax' };
  sort : TableSortModel = { direction: 'desc', field: 'spend_ex_tax' };
  spendExTax = 0;
  spendPoints : Array<ChartPointModel> = [];
  supplierSegments : Array<DonutSegmentModel> = [];
  topSuppliers : Array<IntelligenceTopSupplierModel> = [];
  topSupplierTotals = { credits_ex_tax: 0, invoice_count: 0, order_count: 0, spend_ex_tax: 0 };
  unallocatedCount = 0;
  unallocatedLines : Array<IntelligenceUnallocatedPurchaseModel> = [];
  unallocatedValue = 0;

  agingSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 2 ], key: 'amount', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.SPEND') }
  ];

  compareSeries : Array<ChartSeriesModel> = [];

  spendSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'spend_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.SPEND') },
    { color: CHART_COLORS[ 3 ], key: 'credits_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.CREDITS') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  activePurchaseCount () : number {
    return this.purchaseView === 'all'
      ? this.purchaseLinesCount
      : this.unallocatedCount;
  }

  // Page one of each view arrives with the tab payload, so first pages and view flips
  // are instant; later pages come from the paged endpoint.
  activePurchaseRows () : Array<IntelligenceUnallocatedPurchaseModel> {
    if ( this.purchasesPage > 1 || !this.isDefaultPurchasesSort() ) {
      return this.purchasesPaged;
    }

    return this.purchaseView === 'all'
      ? this.purchaseLines
      : this.unallocatedLines;
  }

  averageSpend ( supplier : IntelligenceTopSupplierModel ) : number {
    return supplier.invoice_count
      ? supplier.spend_ex_tax / supplier.invoice_count
      : 0;
  }

  brandCsvRows () : Array<any> {
    return this.brandRows.map(row => ({
      brand         : row.brand,
      supplier      : row.supplier || this.filters.supplier,
      product_group : row.product_group,
      revenue_ex_tax: row.revenue_ex_tax,
      cost_ex_tax   : row.cost_ex_tax,
      gross_profit  : row.gross_profit,
      margin_pct    : this.marginValue(row.revenue_ex_tax, row.gross_profit)
    }));
  }

  canPageOpenInvoicesNext () : boolean {
    return this.openInvoicesPage * LIST_PAGE_SIZE < this.openInvoicesCount;
  }

  canPagePurchasesNext () : boolean {
    return this.purchasesPage * LIST_PAGE_SIZE < this.activePurchaseCount();
  }

  // The compare supplier rides the same bar, so clearing the filters clears it too.
  clearFilters () : void {
    this.compareSupplier = '';
    this.filters = { brand: '', product_group: '', supplier: '' };

    this.load();
  }

  hasActiveFilters () : boolean {
    return !!this.compareSupplier || Object.keys(this.filters).some(key => !!this.filters[ key ]);

  }

  compareOptions () : Array<string> {
    return ( this.filterOptions.suppliers || [] ).filter(supplier => supplier !== this.filters.supplier);
  }

  comparisonCsvRows () : Array<any> {
    return this.comparisonRows.map(row => ({
      metric                     : row.key.toLowerCase(),
      [ this.filters.supplier ]  : row.a,
      [ this.compareSupplier ]   : row.b,
      difference                 : row.a - row.b
    }));
  }

  comparisonDiff ( row : { a : number; b : number; format : string } ) : string {
    const diff = row.a - row.b;
    const formatted = this.comparisonValue(row, Math.abs(diff));

    return diff < 0
      ? `-${ formatted }`
      : `+${ formatted }`;
  }

  comparisonValue ( row : { format : string }, value : number ) : string {
    return row.format === 'money'
      ? this.money(value)
      : this.count(value);
  }

  count ( value : number ) : string {
    return this.intelligenceFormatService.count(value);
  }

  date ( value : string ) : string {
    return this.intelligenceFormatService.date(value);
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  // The CSV is the full report, not the visible page: every page of the active view
  // and filters is fetched at the export page size, then downloaded as one file. The
  // iteration cap is a runaway guard, not a quota - at 500 a page it allows 25k rows.
  exportOpenInvoicesCsv () : void {
    if ( this.openInvoicesPaging ) {
      return;
    }

    this.openInvoicesPaging = true;

    this.fetchAllPages(page => this.intelligenceService.getPurchasingOpenInvoices(this.filters, page, this.openInvoicesSort, CSV_PAGE_SIZE))
    .then(rows => this.intelligenceFormatService.downloadCsv('open_supplier_invoices', rows.map(row => ({
      supplier      : row.supplier,
      invoice_number: row.invoice_number,
      post_date     : row.post_date,
      age_days      : row.age_days,
      value         : row.value_ex_tax
    }))))
    .catch(() => null)
    .finally(() => this.openInvoicesPaging = false);
  }

  exportPurchasesCsv () : void {
    if ( this.purchasesPaging ) {
      return;
    }

    this.purchasesPaging = true;

    this.fetchAllPages(page => this.intelligenceService.getPurchasingPurchaseLines(this.filters, this.purchaseView, page, this.purchasesSort, CSV_PAGE_SIZE))
    .then(rows => this.intelligenceFormatService.downloadCsv('supplier_purchases', rows.map(row => ({
      supplier : row.supplier,
      item_code: row.item_code,
      item     : row.description,
      quantity : row.quantity,
      post_date: row.post_date,
      value    : row.value_ex_tax
    }))))
    .catch(() => null)
    .finally(() => this.purchasesPaging = false);
  }

  groupComparisonCsvRows () : Array<any> {
    return this.groupComparison.map(row => ({
      product_group                                : row.product_group,
      [ `${ this.filters.supplier } revenue` ]     : row.a_revenue,
      [ `${ this.filters.supplier } profit` ]      : row.a_gross_profit,
      [ `${ this.filters.supplier } margin` ]      : row.a_margin,
      [ `${ this.filters.supplier } avg revenue` ] : row.a_avg_revenue,
      [ `${ this.filters.supplier } avg profit` ]  : row.a_avg_profit,
      [ `${ this.compareSupplier } revenue` ]      : row.b_revenue,
      [ `${ this.compareSupplier } profit` ]       : row.b_gross_profit,
      [ `${ this.compareSupplier } margin` ]       : row.b_margin,
      [ `${ this.compareSupplier } avg revenue` ]  : row.b_avg_revenue,
      [ `${ this.compareSupplier } avg profit` ]   : row.b_avg_profit
    }));
  }

  isAgingEmpty () : boolean {
    return !this.agingPoints.some(point => point.values.amount > 0);
  }

  isBrandsEmpty () : boolean {
    return !this.brandRows.some(row => row.revenue_ex_tax > 0 || row.cost_ex_tax > 0);
  }

  isGroupComparisonEmpty () : boolean {
    return !this.groupComparison.some(row => row.a_revenue > 0 || row.b_revenue > 0);
  }

  isGroupsEmpty () : boolean {
    return this.compareSupplier
      ? !this.groupPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0))
      : !this.groupSegments.some(segment => segment.value > 0);
  }

  // The tab payload carries page one of each list in its default order; anything else
  // comes from the paged endpoint. These say which side of that line a card is on.
  isDefaultOpenInvoicesSort () : boolean {
    return this.openInvoicesSort.field === 'post_date' && this.openInvoicesSort.direction === 'desc';
  }

  isDefaultPurchasesSort () : boolean {
    return this.purchasesSort.field === 'value_ex_tax' && this.purchasesSort.direction === 'desc';
  }

  isOpenInvoicesEmpty () : boolean {
    return !this.openInvoices.length;
  }

  isPurchasesEmpty () : boolean {
    return !this.activePurchaseRows().length;
  }

  isSpendEmpty () : boolean {
    return !this.spendPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isSuppliersEmpty () : boolean {
    return !this.supplierSegments.some(segment => segment.value > 0);
  }

  isTopSuppliersEmpty () : boolean {
    return !this.topSuppliers.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    // The comparison request goes first so the primary supplier's filters are what the
    // tab records as its current view (email reports and the AI summary read those).
    const compareRequest : Promise<IntelligencePurchasingModel> = this.compareSupplier
      ? this.intelligenceService.getPurchasing({ ...this.filters, supplier: this.compareSupplier })
      : Promise.resolve(null);
    const primaryRequest = this.intelligenceService.getPurchasing(this.filters);

    Promise.all([ primaryRequest, compareRequest ])
    .then(( [ data, compare ] : Array<IntelligencePurchasingModel> ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      // Guarded so a frontend that reaches an environment before the backend does
      // keeps its last known lists rather than blanking every dropdown.
      if ( data.filter_options ) {
        this.filterOptions = {
          brands        : data.filter_options.brands || [],
          product_groups: data.filter_options.product_groups || [],
          suppliers     : data.filter_options.suppliers || []
        };

        if ( this.dropFiltersMissingFromOptions() ) {
          this.load();

          return;
        }
      }

      this.creditsExTax = data.credits_ex_tax || 0;
      this.openInvoices = data.open_invoices || [];
      this.openInvoicesCount = data.open_invoices_count || 0;
      this.openOrderCount = data.open_order_count || 0;
      this.openOrderValue = data.open_order_value || 0;
      this.openInvoicesPage = 1;
      this.openInvoicesPaged = [];
      this.openInvoicesSort = { direction: 'desc', field: 'post_date' };
      this.purchaseLines = data.purchase_lines || [];
      this.purchaseLinesCount = data.purchase_lines_count || 0;
      this.purchasesPage = 1;
      this.purchasesPaged = [];
      this.purchasesSort = { direction: 'desc', field: 'value_ex_tax' };
      this.spendExTax = data.spend_ex_tax || 0;
      this.unallocatedCount = data.unallocated_count || 0;
      this.unallocatedLines = data.unallocated_lines || [];
      this.unallocatedValue = data.unallocated_value || 0;

      const aging = data.payables_aging || {};
      const compareAging = compare
        ? compare.payables_aging || {}
        : {};

      if ( compare ) {
        const compareByDate : { [ date : string ] : number } = {};

        ( compare.series || [] ).forEach(day => compareByDate[ day.date ] = day.spend_ex_tax);

        this.compareSeries = [
          { color: CHART_COLORS[ 1 ], key: 'primary', label: this.filters.supplier },
          { color: CHART_COLORS[ 3 ], key: 'compare', label: this.compareSupplier }
        ];
        this.spendSeries = this.compareSeries;
        this.agingSeries = this.compareSeries;

        this.spendPoints = ( data.series || [] ).map(day => ({
          key   : day.date,
          label : this.intelligenceFormatService.date(day.date),
          values: {
            compare: compareByDate[ day.date ] || 0,
            primary: day.spend_ex_tax
          }
        }));

        this.agingPoints = AGING_BUCKETS.map(bucket => ({
          label : this.translateService.instant(`INTELLIGENCE_SPACE.MONEY.AGING_${ bucket.toUpperCase() }`),
          values: {
            compare: compareAging[ bucket ] || 0,
            primary: aging[ bucket ] || 0
          }
        }));

        // Two suppliers cannot be read from one donut, so the group breakdown becomes
        // grouped bars and the supplier donut becomes the split between the two.
        this.groupPoints = this.comparePoints(data.spend_by_group || {}, compare.spend_by_group || {});
        this.supplierSegments = [
          { color: CHART_COLORS[ 1 ], label: this.filters.supplier, value: data.spend_ex_tax || 0 },
          { color: CHART_COLORS[ 3 ], label: this.compareSupplier, value: compare.spend_ex_tax || 0 }
        ];
        this.comparisonRows = this.buildComparisonRows(data, compare);
        this.groupComparison = this.buildGroupComparison(data, compare);
      } else {
        this.compareSeries = [];
        this.spendSeries = [
          { color: CHART_COLORS[ 1 ], key: 'spend_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.SPEND') },
          { color: CHART_COLORS[ 3 ], key: 'credits_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.CREDITS') }
        ];
        this.agingSeries = [
          { color: CHART_COLORS[ 2 ], key: 'amount', label: this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.SPEND') }
        ];

        this.spendPoints = ( data.series || [] ).map(day => ({
          key   : day.date,
          label : this.intelligenceFormatService.date(day.date),
          values: {
            credits_ex_tax: day.credits_ex_tax,
            spend_ex_tax  : day.spend_ex_tax
          }
        }));

        this.agingPoints = AGING_BUCKETS.map(bucket => ({
          label : this.translateService.instant(`INTELLIGENCE_SPACE.MONEY.AGING_${ bucket.toUpperCase() }`),
          values: { amount: aging[ bucket ] || 0 }
        }));

        this.groupPoints = [];
        this.supplierSegments = this.buildSegments(data.spend_by_supplier || {}, this.filterOptions.suppliers);
        this.comparisonRows = [];
        this.groupComparison = [];
      }

      this.groupSegments = this.buildSegments(data.spend_by_group || {}, this.filterOptions.product_groups);

      this.brandRows = this.intelligenceFormatService.sortRows(this.brandPerformance(data, compare), this.brandSort.field, this.brandSort.direction);
      this.brandTotals = this.brandRows.reduce(( totals, row ) => ({
        cost_ex_tax   : totals.cost_ex_tax + row.cost_ex_tax,
        gross_profit  : totals.gross_profit + row.gross_profit,
        revenue_ex_tax: totals.revenue_ex_tax + row.revenue_ex_tax
      }), { cost_ex_tax: 0, gross_profit: 0, revenue_ex_tax: 0 });

      const suppliers = compare
        ? [ ...( data.top_suppliers || [] ), ...( compare.top_suppliers || [] ) ]
        : data.top_suppliers || [];

      this.topSuppliers = this.intelligenceFormatService.sortRows(suppliers, this.sort.field, this.sort.direction);
      this.topSupplierTotals = this.topSuppliers.reduce(( totals, supplier ) => ({
        credits_ex_tax: totals.credits_ex_tax + supplier.credits_ex_tax,
        invoice_count : totals.invoice_count + supplier.invoice_count,
        order_count   : totals.order_count + supplier.order_count,
        spend_ex_tax  : totals.spend_ex_tax + supplier.spend_ex_tax
      }), { credits_ex_tax: 0, invoice_count: 0, order_count: 0, spend_ex_tax: 0 });
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  margin ( revenue : number, grossProfit : number ) : string {
    return this.intelligenceFormatService.percent(this.marginValue(revenue, grossProfit));
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  percent ( value : number ) : string {
    return this.intelligenceFormatService.percent(value);
  }

  openInvoicesCsvRows () : Array<any> {
    return this.openInvoices.map(row => ({
      supplier      : row.supplier,
      invoice_number: row.invoice_number,
      post_date     : row.post_date,
      age_days      : row.age_days,
      value         : row.value_ex_tax
    }));
  }

  openInvoicesRows () : Array<IntelligenceOpenSupplierInvoiceModel> {
    return this.openInvoicesPage > 1 || !this.isDefaultOpenInvoicesSort()
      ? this.openInvoicesPaged
      : this.openInvoices;
  }

  pagerLabel ( page : number, shown : number, total : number ) : string {
    const from = ( page - 1 ) * LIST_PAGE_SIZE + 1;

    return this.translateService.instant('INTELLIGENCE_SPACE.PURCHASING.PAGER_RANGE', {
      from : this.count(from),
      to   : this.count(from + shown - 1),
      total: this.count(total)
    });
  }

  purchasesCsvRows () : Array<any> {
    return this.activePurchaseRows().map(row => ({
      supplier : row.supplier,
      item_code: row.item_code,
      item     : row.description,
      quantity : row.quantity,
      post_date: row.post_date,
      value    : row.value_ex_tax
    }));
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setCompare ( value : string ) : void {
    this.compareSupplier = value === this.filters.supplier
      ? ''
      : value;

    this.load();
  }

  setFilter ( field : keyof IntelligencePurchasingFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    if ( field === 'supplier' && ( !value || value === this.compareSupplier ) ) {
      this.compareSupplier = '';
    }

    this.load();
  }

  setOpenInvoicesPage ( page : number ) : void {
    if ( page < 1 || this.openInvoicesPaging ) {
      return;
    }

    if ( page === 1 && this.isDefaultOpenInvoicesSort() ) {
      this.openInvoicesPage = 1;

      return;
    }

    const generation = this.loadGeneration;

    this.openInvoicesPaging = true;

    this.intelligenceService.getPurchasingOpenInvoices(this.filters, page, this.openInvoicesSort)
    .then(result => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.openInvoicesPaged = result.rows || [];
      this.openInvoicesPage = page;
    })
    .catch(() => null)
    .finally(() => this.openInvoicesPaging = false);
  }

  setPurchasesPage ( page : number ) : void {
    if ( page < 1 || this.purchasesPaging ) {
      return;
    }

    if ( page === 1 && this.isDefaultPurchasesSort() ) {
      this.purchasesPage = 1;

      return;
    }

    const generation = this.loadGeneration;

    this.purchasesPaging = true;

    this.intelligenceService.getPurchasingPurchaseLines(this.filters, this.purchaseView, page, this.purchasesSort)
    .then(result => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.purchasesPaged = result.rows || [];
      this.purchasesPage = page;
    })
    .catch(() => null)
    .finally(() => this.purchasesPaging = false);
  }

  // Flipping the view lands back on page one; with a sort applied the page comes from
  // the endpoint in that order, otherwise it is already in hand from the payload.
  setPurchaseView ( view : string ) : void {
    this.purchaseView = view;

    this.setPurchasesPage(1);
  }

  // The header toggles mirror the other Intelligence tables; the order is applied
  // server side so it holds across every page, and page one refetches unless the
  // card is back on the payload's own default order.
  sortOpenInvoicesBy ( field : string ) : void {
    this.openInvoicesSort = {
      direction: this.openInvoicesSort.field === field && this.openInvoicesSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.setOpenInvoicesPage(1);
  }

  sortPurchasesBy ( field : string ) : void {
    this.purchasesSort = {
      direction: this.purchasesSort.field === field && this.purchasesSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.setPurchasesPage(1);
  }

  sortBrandsBy ( field : string ) : void {
    this.brandSort = {
      direction: this.brandSort.field === field && this.brandSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.brandRows = this.intelligenceFormatService.sortRows(this.brandRows, this.brandSort.field, this.brandSort.direction);
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topSuppliers = this.intelligenceFormatService.sortRows(this.topSuppliers, this.sort.field, this.sort.direction);
  }

  toggleFilter ( field : keyof IntelligencePurchasingFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  topSuppliersCsvRows () : Array<any> {
    return this.topSuppliers.map(supplier => ({
      supplier           : supplier.supplier,
      order_count        : supplier.order_count,
      invoice_count      : supplier.invoice_count,
      spend_ex_tax       : supplier.spend_ex_tax,
      credits_ex_tax     : supplier.credits_ex_tax,
      average_days_to_pay: supplier.average_days_to_pay
    }));
  }

  // Brand rows carry the supplier they came from while comparing, so one table can hold
  // both without the reader having to guess which supplier a row belongs to.
  // The options are read from the response, so they only ever describe the period that
  // was asked for - a new range has its own suppliers, groups and brands. Clearing the
  // active filters as well keeps the rebuilt lists complete, because a filtered
  // response carries only the value that was filtered on.
  // The compare supplier is picked from the same supplier list, so it falls away with
  // the rest. Reports whether anything was dropped: the payload that carried these
  // options was narrowed by a filter this period has no option for, so the caller has
  // to ask again before showing it.
  private dropFiltersMissingFromOptions () : boolean {
    const keptCompare = this.intelligenceFormatService.keepIfOffered(this.compareSupplier, this.filterOptions.suppliers);
    const kept = {
      brand        : this.intelligenceFormatService.keepIfOffered(this.filters.brand, this.filterOptions.brands),
      product_group: this.intelligenceFormatService.keepIfOffered(this.filters.product_group, this.filterOptions.product_groups),
      supplier     : this.intelligenceFormatService.keepIfOffered(this.filters.supplier, this.filterOptions.suppliers)
    };
    const dropped = keptCompare !== this.compareSupplier
      || Object.keys(kept).some(key => kept[ key ] !== this.filters[ key ]);

    this.compareSupplier = keptCompare;
    this.filters = { ...this.filters, ...kept };

    return dropped;
  }

  private fetchAllPages<RowType> ( fetch : ( page : number ) => Promise<IntelligenceListPageModel<RowType>> ) : Promise<Array<RowType>> {
    const rows : Array<RowType> = [];
    const nextPage = ( page : number ) : Promise<Array<RowType>> => fetch(page).then(result => {
      rows.push(...( result.rows || [] ));

      const done = !( result.rows || [] ).length
        || rows.length >= ( result.total || 0 )
        || page >= 50;

      return done
        ? rows
        : nextPage(page + 1);
    });

    return nextPage(1);

  }

  private brandPerformance ( data : IntelligencePurchasingModel, compare : IntelligencePurchasingModel ) : Array<IntelligenceBrandPerformanceModel> {
    if ( !compare ) {
      return data.brand_performance || [];
    }

    return [
      ...( data.brand_performance || [] ).map(row => ({ ...row, supplier: this.filters.supplier })),
      ...( compare.brand_performance || [] ).map(row => ({ ...row, supplier: this.compareSupplier }))
    ];
  }

  // Brand rows roll up to their product group per supplier, so each group can be read as
  // totals and as an average per item — a supplier can win on spend but lose per item.
  private buildGroupComparison ( data : IntelligencePurchasingModel, compare : IntelligencePurchasingModel ) : Array<IntelligenceGroupComparisonModel> {
    const primaryTotals = this.groupTotals(data.brand_performance || []);
    const compareTotals = this.groupTotals(compare.brand_performance || []);
    const groups = Array.from(new Set([ ...Object.keys(primaryTotals), ...Object.keys(compareTotals) ]));

    return groups
      .map(product_group => {
        const a = primaryTotals[ product_group ] || { gross_profit: 0, item_count: 0, revenue: 0 };
        const b = compareTotals[ product_group ] || { gross_profit: 0, item_count: 0, revenue: 0 };

        return {
          a_avg_profit  : this.perItem(a.gross_profit, a.item_count),
          a_avg_revenue : this.perItem(a.revenue, a.item_count),
          a_gross_profit: a.gross_profit,
          a_item_count  : a.item_count,
          a_margin      : this.marginValue(a.revenue, a.gross_profit),
          a_revenue     : a.revenue,
          b_avg_profit  : this.perItem(b.gross_profit, b.item_count),
          b_avg_revenue : this.perItem(b.revenue, b.item_count),
          b_gross_profit: b.gross_profit,
          b_item_count  : b.item_count,
          b_margin      : this.marginValue(b.revenue, b.gross_profit),
          b_revenue     : b.revenue,
          product_group
        };
      })
      .sort(( first, second ) => ( second.a_revenue + second.b_revenue ) - ( first.a_revenue + first.b_revenue ));
  }

  private groupTotals ( rows : Array<IntelligenceBrandPerformanceModel> ) : { [ group : string ] : { gross_profit : number; item_count : number; revenue : number } } {
    return rows.reduce(( totals, row ) => {
      const current = totals[ row.product_group ] || { gross_profit: 0, item_count: 0, revenue: 0 };

      totals[ row.product_group ] = {
        gross_profit: current.gross_profit + row.gross_profit,
        item_count  : current.item_count + ( row.item_count || 0 ),
        revenue     : current.revenue + row.revenue_ex_tax
      };

      return totals;
    }, {} as { [ group : string ] : { gross_profit : number; item_count : number; revenue : number } });
  }

  private perItem ( value : number, count : number ) : number {
    return count
      ? Math.round(value / count)
      : 0;
  }

  private comparePoints ( primary : { [ label : string ] : number }, compare : { [ label : string ] : number } ) : Array<ChartPointModel> {
    const labels = Array.from(new Set([ ...Object.keys(primary), ...Object.keys(compare) ]));

    return labels
      .map(label => ({
        label,
        values: {
          compare: compare[ label ] || 0,
          primary: primary[ label ] || 0
        }
      }))
      .sort(( first, second ) => ( second.values.compare + second.values.primary ) - ( first.values.compare + first.values.primary ));
  }

  // Each response is already filtered to one supplier, so the headline figures and the
  // single top-suppliers row are that supplier's numbers for the period.
  private buildComparisonRows ( data : IntelligencePurchasingModel, compare : IntelligencePurchasingModel ) : Array<{ a : number; b : number; format : string; key : string }> {
    const primaryTop = ( data.top_suppliers || [] )[ 0 ] || {} as IntelligenceTopSupplierModel;
    const compareTop = ( compare.top_suppliers || [] )[ 0 ] || {} as IntelligenceTopSupplierModel;

    return [
      { a: data.spend_ex_tax || 0, b: compare.spend_ex_tax || 0, format: 'money', key: 'SPEND' },
      { a: data.credits_ex_tax || 0, b: compare.credits_ex_tax || 0, format: 'money', key: 'CREDITS' },
      { a: primaryTop.order_count || 0, b: compareTop.order_count || 0, format: 'count', key: 'ORDERS' },
      { a: primaryTop.invoice_count || 0, b: compareTop.invoice_count || 0, format: 'count', key: 'INVOICES' },
      { a: data.open_order_value || 0, b: compare.open_order_value || 0, format: 'money', key: 'OPEN_ORDER_VALUE' },
      { a: primaryTop.average_days_to_pay || 0, b: compareTop.average_days_to_pay || 0, format: 'count', key: 'DAYS_TO_PAY' }
    ];
  }

  private marginValue ( revenue : number, grossProfit : number ) : number {
    return revenue
      ? Math.round(( grossProfit / revenue ) * 1000) / 10
      : null;
  }

  private buildSegments ( amounts : { [ label : string ] : number }, options : Array<string> ) : Array<DonutSegmentModel> {
    const entries = Object.keys(amounts)
      .map(label => ({ amount: amounts[ label ] || 0, label }))
      .sort(( first, second ) => second.amount - first.amount);

    const kept = entries.slice(0, DONUT_COLORS.length);
    const remainder = entries.slice(DONUT_COLORS.length).reduce(( sum, entry ) => sum + entry.amount, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( entry, index ) => ({
      color    : donutColor(entry.label, options, index),
      drillable: true,
      key      : entry.label,
      label    : entry.label,
      value    : entry.amount
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : DONUT_OTHER_COLOR,
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: entries.slice(DONUT_COLORS.length).map(( entry, index ) => ({
          color    : donutColor(entry.label, options, DONUT_COLORS.length + index),
          drillable: true,
          key      : entry.label,
          label    : entry.label,
          value    : entry.amount
        })),
        value : remainder
      });
    }

    return segments;
  }
}
